/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    ChannelDeliveryOptionConfiguration,
    ProductDeliveryOptions,
    SalesLine,
    SalesOrder,
    SimpleProduct,
    TransactionType
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import get from 'lodash/get';
import { computed, when } from 'mobx';
import * as React from 'react';

import {
    IGetOrderSummaryInput,
    IHelp,
    IOrderSummary,
    IPaymentMethods,
    IPaymentMethodsInput
} from '@msdyn365-commerce-modules/order-management';
import { getGroups, IGroups } from '@msdyn365-commerce-modules/order-management';
import { getOrderInformation, getOrderInformationInput, IOrderInformation } from '@msdyn365-commerce-modules/order-management';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { getSalesLines, getSalesLinesInput } from '@msdyn365-commerce-modules/order-management';
import { Label } from '@msdyn365-commerce-modules/order-management';
import { getTenderIdOperationIdMap, IPriceContext } from '@msdyn365-commerce-modules/order-management';
import { getOrderConfirmationHelp } from './components/get-help';
import { getOrderConfirmationOrderSummary } from './components/get-order-summary';
import { getOrderConfirmationPaymentMethods } from './components/get-payment-methods';
import { IPgrB2BOrderConfirmationData } from './pgr_b2b_order_confirmation.data';
import { IPgr_b2b_order_confirmationProps } from './pgr_b2b_order_confirmation.props.autogenerated';
import axios from 'axios';
import { getBMAStockistAsync } from '../../dataActions/DataActionExtension.g';

export interface IOrderConfirmationViewProps extends IPgr_b2b_order_confirmationProps<IPgrB2BOrderConfirmationData> {
    moduleProps: IModuleProps;
    salesOrder?: SalesOrder;
    footerProps?: INodeProps;
    alert?: React.ReactNode;
    loading?: React.ReactNode;
    heading: React.ReactNode;
    backToShoppingLink?: React.ReactNode;
    orderInfomation?: IOrderInformation;
    salesLines?: ISalesLine[];
    groups?: IGroups;
    orderSummary?: IOrderSummary;
    payment?: IPaymentMethods;
    help?: IHelp;
    state?: any;
    props?: any;
}

/**
 *
 * OrderConfirmation component.
 * @extends {React.PureComponent<IPgr_b2b_order_confirmationProps<IPgrB2BOrderConfirmationData>>}
 */
class OrderConfirmation extends React.PureComponent<IPgr_b2b_order_confirmationProps<IPgrB2BOrderConfirmationData>> {
    private readonly moduleName: string = 'ms-order-confirmation';

    private readonly telemetryContent?: ITelemetryContent;

    private readonly backToShoppingAttributes?: Msdyn365.IDictionary<string>;

    constructor(props: IPgr_b2b_order_confirmationProps<IPgrB2BOrderConfirmationData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.BackToShopping);
        this.backToShoppingAttributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        this.state = {
            updatedSalesLines: [],
            totalAmount: 0
        };
    }

    @computed get isCurrentChannel(): boolean {
        if (!this.order) {
            return false;
        }
        return this.order.ChannelId === get(this.props, 'context.request.apiSettings.channelId');
    }

    @computed get isDataReady(): boolean {
        return get(this.props, 'data.orderHydration.status') === 'SUCCESS';
    }

    @computed get hasError(): boolean {
        return get(this.props, 'data.orderHydration.status') === 'FAILED';
    }

    @computed get order(): SalesOrder | undefined {
        return get(this.props, 'data.orderHydration.result.salesOrder');
    }

    @computed get products(): SimpleProduct[] {
        return get(this.props, 'data.orderHydration.result.products') || [];
    }

    @computed get deliveryOptions(): ProductDeliveryOptions[] {
        return get(this.props, 'data.orderHydration.result.deliveryOptions') || [];
    }

    @computed get priceContext(): IPriceContext {
        const {
            id,
            typeName,
            context,
            telemetry,
            resources: { freePriceText }
        } = this.props;
        return {
            id,
            typeName,
            context,
            telemetry,
            freePriceText
        };
    }

    public componentDidMount() {
        when(
            () => {
                return !!(this.order?.SalesLines && this.order.SalesLines.length > 0 && this.products && this.products.length > 0);
            },
            async () => {
                const updatedSalesLines: any =
                    this.order && this.order.SalesLines && this.order.SalesLines.length > 0 ? this.order.SalesLines : [];
                const jwellerInfo = await getBMAStockistAsync({
                    callerContext: this.props.context.actionContext,
                    queryResultSettings: {}
                });
                let totalAmount = 0;
                for (const salesLine of updatedSalesLines) {
                    if (salesLine.ExtensionProperties && salesLine.ExtensionProperties.length > 0) {
                        const snapshotProperty = salesLine.ExtensionProperties?.find((e: any) => {
                            return e.Key === 'AAA_SNAPSHOTID' && e.Value && e.Value.StringValue;
                        });
                        if (snapshotProperty && snapshotProperty.Value && snapshotProperty.Value.StringValue) {
                            const bmaImageWithFormatEndPoint = this.props.app.config.bmaImageEndPoint.replace(
                                '#snapshotId',
                                snapshotProperty.Value.StringValue
                            );

                            const customerReference = salesLine.ExtensionProperties?.find((e: any) => {
                                return e.Key === 'AAA_RETAILB2CNAME' && e.Value && e.Value.StringValue;
                            });
                            salesLine.customerReference = customerReference
                                ? customerReference.Value.StringValue
                                    ? customerReference.Value.StringValue
                                    : ''
                                : '';
                            salesLine.snapshotProperty = snapshotProperty.Value.StringValue;
                            const product = this.products.find(product => {
                                return product.RecordId === salesLine.ProductId;
                            });
                            salesLine.productName = product ? product.Name : '';
                            const image = await axios.get(`${this.props.app.config.bmaBaseUrl}${bmaImageWithFormatEndPoint}`);
                            salesLine.BmaImageUrl = image.data[0];
                            const bmaReviewEndPoint = this.props.app.config.bmaReviewEndPoint.replace(
                                '#snapshotId',
                                snapshotProperty.Value.StringValue
                            );
                            let bmaPriceEndPoint = this.props.app.config.bmaPriceEndPoint.replace(
                                '#snapshotId',
                                snapshotProperty.Value.StringValue
                            );
                            bmaPriceEndPoint = bmaPriceEndPoint.replace('#jewellerId', jwellerInfo.BMARetailStockist);
                            const price = await axios.get(`${this.props.app.config.bmaBaseUrl}${bmaPriceEndPoint}`);
                            const ringsInfo = price.data.rings;
                            salesLine.ItemInfo = { ...salesLine.ItemInfo, priceAmount: 0, rings: [] };
                            salesLine.ItemInfo.priceAmount = price.data.sum.vk;
                            const review = await axios.get(`${this.props.app.config.bmaBaseUrl}${bmaReviewEndPoint}`);
                            salesLine.ItemInfo.review = review.data;
                            salesLine.ItemInfo.quantity = salesLine.Quantity;
                            for (const ring of ringsInfo) {
                                const ringReview = salesLine.ItemInfo.review.find((r: any) => {
                                    return r.ringId === ring.id;
                                });
                                let height = '5mm';
                                let width = '1.4mm';
                                if (ringReview.properties && ringReview.properties.profile) {
                                    if (ringReview.properties.profile.width) {
                                        width = `${ringReview.properties.profile.width.value} ${ringReview.properties.profile.width.type}`;
                                    }
                                    if (ringReview.properties.profile.height) {
                                        height = `${ringReview.properties.profile.height.value} ${ringReview.properties.profile.height.type}`;
                                    }
                                }
                                let diamondCount = null;
                                diamondCount =
                                    ringReview.properties.stone.preset.length > 0 &&
                                    ringReview.properties.stone.preset[0].id !== 'none' &&
                                    ringReview.properties.stone.quantity.reduce(function(sum: any, current: any) {
                                        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                                        return sum + current.count;
                                    }, 0);
                                let translationInfo = await axios.get(
                                    `${this.props.app.config.bmaBaseUrl}${this.props.app.config.bmaTranslationMessageEndPoint}`
                                );
                                translationInfo = translationInfo.data;
                                const ringSizePi = ringReview.properties.ring.size.value / 3.14159265;
                                const ringFineness = ringReview.properties.disc.fineness.map((fineness: any) => {
                                    return `${translationInfo[fineness.label]}`;
                                });
                                const ringColor = ringReview.properties.disc.color.map((color: any) => {
                                    return `${translationInfo[color.label]}`;
                                });
                                const ringPolish = ringReview.properties.disc.surface.map((polish: any) => {
                                    return `${translationInfo[polish.label]}`;
                                });
                                const ringFinenessColorPolish = ringColor.map((color: any, index: number) => {
                                    // eslint-disable-next-line security/detect-object-injection
                                    return `${ringFineness[index]} ${color} ${ringPolish[index]}`;
                                });
                                const ringInfo: any = {
                                    ringId: ring.id,
                                    ringName: ringReview.name,
                                    alloy: `${translationInfo[ringReview.properties.disc.fineness[0].label]}
                                ${''}${translationInfo[ringReview.properties.disc.color[0].label]}`,
                                    sizeLabel: ringReview.properties.ring.size.label,
                                    sizeValue:
                                        Math.round(
                                            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                                            (ringSizePi + Number.EPSILON) * 100
                                        ) / 100,
                                    specifications: diamondCount
                                        ? `${diamondCount} Diamonds, ${width} Wide, ${height} thick`
                                        : `${width} Wide, ${height} thick`,
                                    engraving: ringReview.properties.eSpot.text.inner,
                                    ringFineness: ringFineness,
                                    ringColor: ringColor,
                                    ringPolish: ringPolish,
                                    ringFinenessColorPolishInfo: ringFinenessColorPolish
                                };
                                ringInfo.priceAmount = ring.vk;
                                totalAmount = totalAmount + ring.vk;
                                salesLine.ItemInfo.rings.push(ringInfo);
                            }
                        }
                    }
                }
                this.setState({ totalAmount });
                this.setState({ updatedSalesLines });
            }
        );
    }

    @computed get earnedPoints(): number {
        const loyaltyCard = this.props.data.loyaltyCard.result;
        if (loyaltyCard && loyaltyCard.RewardPoints && this.order) {
            const pointPrograms = new Set(loyaltyCard.RewardPoints.map(point => point.RewardPointId));
            const rewardLines = this.order.LoyaltyRewardPointLines || [];
            let total = 0;
            rewardLines.forEach(line => {
                if ((line.RewardPointAmountQuantity || 0) > 0 && pointPrograms.has(line.RewardPointId)) {
                    total += line.RewardPointAmountQuantity!;
                }
            });
            return total;
        }
        return 0;
    }

    @computed get orderSummaryProps(): IGetOrderSummaryInput | undefined {
        if (!this.order) {
            return;
        }
        const { request } = this.props.context;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const { channelDeliveryOptionConfig, featureState } = this.props?.data;
        const retailMultiplePickUpOptionEnabled = featureState?.result?.find(item => item.Name === multiplePickupStoreSwitchName)
            ?.IsEnabled;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;
        const orderDetailsLines = this.order?.SalesLines;
        const canShip = orderDetailsLines?.some(orderDetailsLine =>
            orderDetailsLine.DeliveryMode && orderDetailsLine.DeliveryMode !== ''
                ? orderDetailsLine.DeliveryMode !==
                      this.getDeliveryMode(
                          orderDetailsLine,
                          retailMultiplePickUpOptionEnabled,
                          channelDeliveryOptionConfig?.result,
                          pickupDeliveryModeCode
                      ) && orderDetailsLine.DeliveryMode !== emailDeliveryModeCode
                : orderDetailsLine
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app configs are of generic type
        const isShowOrHideTaxEnabled = request.app.config.isEnableShowOrHideSalesTaxECommerceEnabled === true;

        const {
            resources: {
                orderSummaryHeading,
                orderSummaryItemsTotalLabel,
                orderSummaryShippingFeeLabel,
                orderSummaryTaxLabel,
                orderSummaryGrandTotalLabel,
                pointsEarnedLabel
            }
        } = this.props;
        return {
            order: this.order,
            earnedPoints: this.earnedPoints,
            priceContext: this.priceContext,
            resource: {
                orderSummaryHeading,
                orderSummaryItemsTotalLabel,
                orderSummaryShippingFeeLabel,
                orderSummaryTaxLabel,
                pointsEarnedLabel,
                orderSummaryGrandTotalLabel
            },
            canShip,
            isTaxIncludedInPrice: this.order.IsTaxIncludedInPrice,
            isShowTaxBreakUp: isShowOrHideTaxEnabled
        };
    }

    @computed get paymentMethodsProps(): IPaymentMethodsInput | undefined {
        const tenderTypes = this.props.data.tenderTypes.result;
        if (!this.order || !this.order.TenderLines || !tenderTypes) {
            return;
        }

        const locale = get(this.props, 'context.request.locale') || 'en-US';

        const {
            resources: {
                paymentMethodsTitle,
                creditCardEndingLabel,
                giftCardEndingLabel,
                amountCoveredLabel,
                cashUsedLabel,
                loyaltyCardUsedLabel,
                customerAccountUsedLabel
            }
        } = this.props;

        return {
            priceContext: this.priceContext,
            tenderLines: this.order.TenderLines,
            tenderIdOperationIdMap: getTenderIdOperationIdMap(tenderTypes),
            locale,
            resource: {
                paymentMethodsTitle,
                creditCardEndingLabel,
                giftCardEndingLabel,
                amountCoveredLabel,
                cashUsedLabel,
                loyaltyCardUsedLabel,
                customerAccountUsedLabel
            }
        };
    }

    public render(): JSX.Element {
        const {
            resources: {
                noSalesOrderText,
                genericErrorMessage,
                needHelpLabel,
                helpLineNumberLabel,
                loadingLabel,
                backToShopping,
                orderItemLabel,
                orderItemsLabel,
                pickupItemsHeader,
                pickingItemsLabel,
                shippingAddressTitle,
                shippingItemToYouLabel,
                helpLineContactAriaLabel,
                emailItemsHeader,
                emailingItemsLabel
            },
            config: { heading, contactNumber, shouldShowQrCode, showTimeslot }
        } = this.props;

        const moduleProps = { moduleProps: this.props, className: classname('ms-order-confirmation', this.props.config.className) };
        const backToShoppingUrl = Msdyn365.getUrlSync('home', this.props.context.actionContext) || '';
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const { channelDeliveryOptionConfig, featureState } = this.props?.data;
        const retailMultiplePickUpOptionEnabled = featureState?.result?.find(item => item.Name === multiplePickupStoreSwitchName)
            ?.IsEnabled;

        let viewProps: IOrderConfirmationViewProps = {
            ...this.props,
            moduleProps,
            state: this.state,
            props: this.props,
            heading: heading && heading.text && (
                <Msdyn365.Text
                    {...heading}
                    className='ms-order-confirmation__heading'
                    text={heading.text}
                    tag={heading.tag || 'h2'}
                    editProps={{ onEdit: this.handleOrderConfirmationHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            backToShoppingLink: backToShoppingUrl && (
                <Button className='ms-order-confirmation__btn-keep-shopping' href={backToShoppingUrl} {...this.backToShoppingAttributes}>
                    {backToShopping}
                </Button>
            ),
            footerProps: { className: 'ms-order-confirmation__footer' }
        };

        if (this.hasError) {
            viewProps = {
                ...viewProps,
                alert: <Label className='ms-order-confirmation__alert' label={genericErrorMessage} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!this.isDataReady) {
            viewProps = {
                ...viewProps,
                loading: <Label className='ms-order-confirmation__loading' label={loadingLabel} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!this.order) {
            viewProps = {
                ...viewProps,
                alert: <Label className='ms-order-confirmation__alert' label={noSalesOrderText} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        const isSalesInvoice = this.order.TransactionTypeValue === TransactionType.SalesInvoice;

        const salesLinesInput = getSalesLinesInput(
            this.props,
            this.moduleName,
            this.order,
            this.products,
            this.priceContext,
            true,
            showTimeslot,
            undefined,
            retailMultiplePickUpOptionEnabled,
            channelDeliveryOptionConfig?.result,
            this.deliveryOptions
        );
        const salesLines = salesLinesInput && getSalesLines(salesLinesInput);
        const orderInformationInput = getOrderInformationInput(
            this.props,
            this.moduleName,
            this.order,
            this.priceContext,
            undefined,
            undefined,
            undefined,
            isSalesInvoice,
            shouldShowQrCode ?? true
        );
        const orderInfomation = getOrderInformation(orderInformationInput);

        viewProps = {
            ...viewProps,
            salesOrder: this.order,
            orderInfomation,
            salesLines,
            groups: salesLines
                ? getGroups({
                      moduleName: this.moduleName,
                      salesLines,
                      resources: {
                          orderItemLabel,
                          orderItemsLabel,
                          pickupItemsHeader,
                          pickingItemsLabel,
                          shippingAddressTitle,
                          shippingItemToYouLabel,
                          emailItemsHeader,
                          emailingItemsLabel
                      },
                      showTimeslot,
                      isConfirmationView: true,
                      retailMultiplePickUpOptionEnabled
                  })
                : undefined,
            orderSummary: !isSalesInvoice && this.orderSummaryProps ? getOrderConfirmationOrderSummary(this.orderSummaryProps) : undefined,
            payment: !isSalesInvoice && this.paymentMethodsProps ? getOrderConfirmationPaymentMethods(this.paymentMethodsProps) : undefined,
            help:
                !isSalesInvoice && contactNumber
                    ? getOrderConfirmationHelp({
                          needHelpLabel,
                          helpLineNumberLabel,
                          contactNumber,
                          helpLineContactAriaLabel,
                          telemetryContent: this.telemetryContent
                      })
                    : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleOrderConfirmationHeadingChange = (event: Msdyn365.ContentEditableEvent) =>
        (this.props.config.heading!.text = event.target.value);

    private readonly getDeliveryMode = (
        salesLine: SalesLine,
        featureSate: boolean = false,
        channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration,
        pickupDeliveryMode?: string
    ) => {
        if (!featureSate) {
            return pickupDeliveryMode;
        }
        return channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(deliveryMode => deliveryMode === salesLine.DeliveryMode);
    };
}

export default OrderConfirmation;
